import * as React from 'react'
import { useMemo } from 'react'
import { Formik, Field } from 'formik'
import Link from 'next/link'
import { useTranslations } from 'next-intl'

import { Button, TextInputField } from './form'

import { colors, fonts } from '../utils/style'
import {
  createMobileRegistrationFormFirstPart,
  createMobileRegistrationFormSecondPart,
} from '../utils/validation'

type Props = {
  onSubmit: (values: any, formikActions: any) => void
}

export default function MobileRegistrationForm(props: Props) {
  const t = useTranslations()
  const [submittedFirstStep, setSubmittedFirstStep] = React.useState(false)
  const firstPartSchema = useMemo(() => createMobileRegistrationFormFirstPart(t), [t])
  const secondPartSchema = useMemo(() => createMobileRegistrationFormSecondPart(t), [t])
  const validationSchema = submittedFirstStep
    ? secondPartSchema
    : firstPartSchema

  return (
    <React.Fragment>
      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          password: '',
          passwordConfirmation: '',
        }}
        validationSchema={validationSchema}
        onSubmit={props.onSubmit}
      >
        {(formProps) => (
          <form
            key={submittedFirstStep.toString()}
            onSubmit={formProps.handleSubmit}
            noValidate
          >
            <h1>{t('registrationPage.title')}</h1>

            {!submittedFirstStep ? (
              <React.Fragment>
                <div className="field">
                  <Field
                    name="firstName"
                    component={TextInputField}
                    type="text"
                    label={t('registrationPage.form.firstName')}
                    autoTransliterate
                  />
                </div>
                <div className="field">
                  <Field
                    name="lastName"
                    component={TextInputField}
                    type="text"
                    label={t('registrationPage.form.lastName')}
                    autoTransliterate
                  />
                </div>
                <div className="field">
                  <Field
                    name="phone"
                    component={TextInputField}
                    type="text"
                    label={t('registrationPage.form.phone')}
                  />
                </div>
                <div className="actions mobile">
                  <div className="secondary-action">
                    <div className="label">
                      {t('authSecondaryActions.alreadyRegistered')}
                    </div>
                    <Link href="/" className="login-link">
                      {t('authSecondaryActions.login')}
                    </Link>
                  </div>
                  <div className="primary-action">
                    <Button
                      disabled={!formProps.isValid}
                      onClick={() => setSubmittedFirstStep(true)}
                      type="button"
                    >
                      {t('registrationPage.mobile.next')}
                    </Button>
                  </div>
                </div>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <button
                  className="back-btn"
                  onClick={() => setSubmittedFirstStep(false)}
                  type="button"
                >
                  <svg width="10" height="6">
                    <use href="#ic-chevron" />
                  </svg>
                  {t('registrationPage.mobile.back')}
                </button>

                <div className="field">
                  <Field
                    name="email"
                    component={TextInputField}
                    type="email"
                    label={t('registrationPage.form.email')}
                  />
                </div>
                <div className="field">
                  <Field
                    name="password"
                    component={TextInputField}
                    type="password"
                    label={t('registrationPage.form.password')}
                  />
                </div>
                <div className="field">
                  <Field
                    name="passwordConfirmation"
                    component={TextInputField}
                    type="password"
                    label={t('registrationPage.form.passwordConfirmation')}
                  />
                </div>

                <div className="actions">
                  <div className="secondary-action">
                    <div className="label">
                      {t('authSecondaryActions.alreadyRegistered')}
                    </div>
                    <Link href="/" className="login-link">
                      {t('authSecondaryActions.login')}
                    </Link>
                  </div>
                  <div className="primary-action">
                    <Button
                      disabled={!formProps.isValid || formProps.isSubmitting}
                      onClick={() => formProps.handleSubmit()}
                      type="submit"
                    >
                      {formProps.isSubmitting
                        ? t('registrationPage.form.submitting')
                        : t('registrationPage.form.submit')}
                    </Button>
                  </div>
                </div>

                <p className="terms">{t('registrationPage.termsNotice')}</p>
              </React.Fragment>
            )}
          </form>
        )}
      </Formik>
      <style jsx>{`
        form {
          position: relative;
          z-index: 90;

          width: 100%;
          max-width: 377px;
          margin: 0 auto;
        }

        h1 {
          margin: 0 0 34px 0;

          font-family: ${fonts.IBMPlex};
          font-weight: bold;
          line-height: 28px;
          font-size: 24px;
          text-align: left;

          color: #14145a;
        }

        .field {
          margin-bottom: 10px;
        }

        .actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin: 17px 0 19px;
        }

        .actions a {
          font-family: ${fonts.IBMPlex};
          font-size: 14px;
          line-height: 1;

          color: ${colors.grayText};
        }

        .actions a.login-link {
          font-size: 14px;
        }

        .secondary-action .label {
          margin-bottom: -3px;
          font-family: ${fonts.IBMPlex};
          font-size: 12px;
          line-height: 1;

          color: ${colors.inputText};
        }

        .terms {
          margin: 21px 0;
          font-size: 12px;
          line-height: 18px;
        }

        .back-btn {
          display: flex;
          align-items: center;

          border: 0;
          padding: 15px;

          position: absolute;
          left: -15px;
          top: -64px;

          text-decoration: underline;
          font-family: ${fonts.IBMPlex};
          font-size: 14px;
          line-height: 18px;

          color: ${colors.grayText};
          background: none;

          cursor: pointer;
        }

        .back-btn svg {
          margin-right: 9px;
          transform: rotate(90deg);
        }
      `}</style>
    </React.Fragment>
  )
}
